import React, { useState } from "react";
import { Button } from "antd";
import { Plus } from "lucide-react";
import { DataTable } from "../../components/DataTable";
import { IReview } from "../../../types/api/review";
import { useReviewList, useReviewDelete } from "../../../hooks/api/review/hooks";
import { getReviewColumns } from "./ReviewColumns";
import ReviewModal from "./ReviewModal";

import { useMessageApi } from "../../../provider/AntdNotification";

const ReviewList: React.FC = () => {
  const messageApi = useMessageApi();
  const [qData, setQdata] = useState<{
    page: number;
    limit: number;
    search?: string;
    status?: string;
  }>({
    page: 1,
    limit: 10,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<IReview | null>(null);

  const { data, isLoading, isError } = useReviewList(qData);
  const deleteMutation = useReviewDelete({
    qData,
    onSuccess: () => messageApi.success("Review deleted successfully"),
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to delete review"),
  });

  const handleAdd = () => {
    setSelectedReview(null);
    setModalOpen(true);
  };

  const handleEdit = (record: IReview) => {
    setSelectedReview(record);
    setModalOpen(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const columns = getReviewColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
  });

  return (
    <div className="w-full">
      <DataTable
        title={<h1 className="text-xl font-semibold text-slate-800">Customer Reviews</h1>}
        headerActions={
          <Button
            type="primary"
            size="small"
            icon={<Plus size={16} />}
            onClick={handleAdd}
            className="!bg-primary flex items-center gap-2"
          >
            Add Review
          </Button>
        }
        columns={columns}
        dataSource={data?.data || []}
        rowKey="_id"
        loading={isLoading}
        isError={isError}
        searchProps={{
          searchValue: qData.search,
          onSearchChange: (v) =>
            setQdata((prev) => ({ ...prev, search: v, page: 1 })),
          placeholder: "Search reviews...",
          statusValue: qData.status,
          onStatusChange: (v) =>
            setQdata((prev) => ({ ...prev, status: v, page: 1 })),
        }}
        paginationProps={{
          page: qData.page,
          limit: qData.limit,
          total: data?.extra?.total || 0,
          onChange: (page, limit) =>
            setQdata((prev) =>
              limit !== prev.limit ? { ...prev, page: 1, limit } : { ...prev, page },
            ),
        }}
      />
      <ReviewModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedItem={selectedReview}
        qData={qData}
      />
    </div>
  );
};

export default ReviewList;
